import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react'
import { Formik, FieldArray, Field } from 'formik'
import * as Yup from 'yup'
import { useDrag, useDrop } from 'react-dnd'
import {
  PdfPreviewModal,
  downloadTravelChecklistPdf,
  buildTravelChecklistPdfHtml,
  useErrorService,
} from '@abroad/components'
import { ExperienceInputField } from '../form'
import QuestHOC from './QuestHOC'
import ExperienceDeleteIcon from './ExperienceDeleteIcon'
import ExperienceConfirmModal from './ExperienceConfirmModal'
import {
  queueAutosaveOnBlur,
  runQueuedFormikSave,
  FormikDirtyReporter,
} from './questAutosave'

const SECTION_DND = 'CHECKLIST_SECTION'
const ITEM_DND = 'CHECKLIST_ITEM'

const truncateLabel = (text) => {
  const t = String(text || '').trim()
  if (t.length > 72) return `${t.slice(0, 69)}\u2026`
  return t
}

/** Title-case ALL CAPS section labels so they match the sample UI. */
const toChecklistTitleCase = (text) => {
  const t = String(text || '')
  if (!t.trim()) return t
  const letters = t.replace(/[^a-zA-Z]/g, '')
  // Leave mixed / sentence case alone; only fix screaming ALL CAPS.
  if (!letters || letters !== letters.toUpperCase()) return t
  return t
    .toLowerCase()
    .replace(
      /\b([a-z0-9])([^\s]*)/g,
      (_, first, rest) => first.toUpperCase() + rest,
    )
}

const normalizeChecklistSections = (sections) =>
  (sections || []).map((section) => ({
    ...section,
    title: toChecklistTitleCase(section?.title),
  }))

const defaultValues = {
  description: '',
  sections: [
    {
      title: '',
      items: [{ item: '' }],
      notes: '',
    },
  ],
}

const sectionItemSchema = Yup.object().shape({
  item: Yup.string(),
})

const sectionSchema = Yup.object().shape({
  title: Yup.string().when('items', {
    is: (items) =>
      items && items.some((item) => item.item && item.item.trim() !== ''),
    then: Yup.string().required(
      'Section title is required when items are added',
    ),
    otherwise: Yup.string(),
  }),
  items: Yup.array().of(sectionItemSchema),
  notes: Yup.string(),
})

const validationSchema = Yup.object().shape({
  description: Yup.string(),
  sections: Yup.array().of(sectionSchema),
})

const addItemIds = (sections) => {
  return sections.map((section) => ({
    ...section,
    items: section.items.map((item, index) => ({
      ...item,
      _tempId: item._tempId || `item-${Date.now()}-${index}`,
    })),
  }))
}

const cleanItemsForAPI = (sections) => {
  return sections.map((section) => ({
    ...section,
    items: section.items.map((item) => {
      const { _tempId, ...cleanItem } = item
      return cleanItem
    }),
  }))
}

const buildChecklistDataForPdf = (values) => {
  const sections = values.sections
    .map((section) => ({
      title: section.title,
      items: section.items.filter(
        (item) => item.item && item.item.trim() !== '',
      ),
      notes: section.notes,
    }))
    .filter((section) => {
      return (
        section.title?.trim() !== '' ||
        section.notes?.trim() !== '' ||
        section.items.length > 0
      )
    })

  return {
    description: values.description,
    sections,
  }
}

const hasChecklistContent = (checklistData) =>
  Boolean(
    checklistData?.description ||
      (checklistData?.sections?.length > 0 &&
        checklistData.sections.some(
          (section) =>
            section.title ||
            section.notes ||
            (section.items && section.items.length > 0),
        )),
  )

const buildSubmitPayload = (values) => {
  const filteredSections = cleanItemsForAPI(
    values.sections
      .map((section) => ({
        title: (section.title || '').trim(),
        items: section.items.filter(
          (item) => item.item && item.item.trim() !== '',
        ),
        notes: section.notes,
      }))
      .filter((section) => {
        return (
          section.title !== '' ||
          section.notes?.trim() !== '' ||
          section.items.length > 0
        )
      }),
  )

  return {
    travelChecklist: {
      description: values.description,
      sections: filteredSections,
    },
  }
}

const filterChecklistSections = (sections) =>
  cleanItemsForAPI(
    sections
      .map((section) => ({
        title: (section.title || '').trim(),
        items: section.items.filter(
          (item) => item.item && item.item.trim() !== '',
        ),
        notes: section.notes,
      }))
      .filter((section) => {
        return (
          section.title !== '' ||
          section.notes?.trim() !== '' ||
          section.items.length > 0
        )
      }),
  )

const buildChecklistApiPayload = (values) => ({
  travelChecklist: {
    description: values.description,
    sections: filterChecklistSections(values.sections),
  },
})

/** Keep editor rows usable after save — never drop an open draft section. */
const ensureChecklistFormShape = (values) => {
  const sections =
    Array.isArray(values?.sections) && values.sections.length > 0
      ? values.sections
      : defaultValues.sections
  return {
    description: values?.description || '',
    sections: addItemIds(
      sections.map((section) => ({
        title: section?.title || '',
        notes: section?.notes || '',
        items:
          Array.isArray(section?.items) && section.items.length > 0
            ? section.items
            : [{ item: '' }],
      })),
    ),
  }
}

const itemLabel = (n) => `${n} item${n === 1 ? '' : 's'}`

const ChecklistItemRow = ({
  sectionIndex,
  itemIndex,
  moveItem,
  onDragEnd,
  onRemove,
  canRemove,
  fieldBlur,
  disabled = false,
}) => {
  const ref = useRef(null)

  const [{ handlerId }, drop] = useDrop({
    accept: ITEM_DND,
    collect(monitor) {
      return { handlerId: monitor.getHandlerId() }
    },
    hover(dragItem) {
      if (disabled || !ref.current) return
      if (dragItem.sectionIndex !== sectionIndex) return
      const dragIndex = dragItem.index
      const hoverIndex = itemIndex
      if (dragIndex === hoverIndex) return
      moveItem(dragIndex, hoverIndex)
      dragItem.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: ITEM_DND,
    item: () => ({ index: itemIndex, sectionIndex }),
    canDrag: !disabled,
    end() {
      if (!disabled && typeof onDragEnd === 'function') onDragEnd()
    },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  })

  drag(drop(ref))

  return (
    <div
      ref={ref}
      data-handler-id={handlerId}
      className={`experience-cl-item${isDragging ? ' dragging' : ''}`}>
      <span className='experience-grab' title='Drag to reorder'>
        ⋮⋮
      </span>
      <span className='experience-cl-box' aria-hidden='true' />
      <Field
        name={`sections[${sectionIndex}].items[${itemIndex}].item`}
        placeholder='Checklist item'
        onBlur={fieldBlur}
        disabled={disabled}
      />
      {canRemove ? (
        <button
          type='button'
          className='experience-x'
          title='Delete item'
          aria-label='Delete item'
          disabled={disabled}
          onClick={onRemove}>
          <ExperienceDeleteIcon />
        </button>
      ) : null}
    </div>
  )
}

const ChecklistSection = ({
  section,
  sectionIndex,
  openIndex,
  setOpenIndex,
  moveSection,
  onSectionDragEnd,
  removeSection,
  canRemoveSection,
  onRemoveItem,
  onItemDragEnd,
  fieldBlur,
  disabled = false,
}) => {
  const ref = useRef(null)
  const isOpen = openIndex === sectionIndex
  const itemCount = section.items?.length || 0

  const [{ handlerId }, drop] = useDrop({
    accept: SECTION_DND,
    collect(monitor) {
      return { handlerId: monitor.getHandlerId() }
    },
    hover(dragItem) {
      if (disabled || !ref.current) return
      const dragIndex = dragItem.index
      const hoverIndex = sectionIndex
      if (dragIndex === hoverIndex) return
      moveSection(dragIndex, hoverIndex)
      dragItem.index = hoverIndex
    },
  })

  const [{ isDragging }, drag, preview] = useDrag({
    type: SECTION_DND,
    item: () => ({ index: sectionIndex }),
    canDrag: !disabled,
    end() {
      if (!disabled && typeof onSectionDragEnd === 'function') {
        onSectionDragEnd()
      }
    },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  })

  preview(drop(ref))

  return (
    <div
      ref={ref}
      data-handler-id={handlerId}
      className={`experience-cl-sec${isOpen ? ' open' : ''}${
        isDragging ? ' dragging' : ''
      }`}>
      <div
        className='experience-cl-sec-head'
        onClick={(e) => {
          if (disabled) return
          // Match HTML: click header to toggle, except grab / title / delete / caret
          if (
            e.target.closest(
              '.experience-grab, .experience-cl-title, .experience-x, .experience-cl-caret, input, textarea',
            )
          ) {
            return
          }
          setOpenIndex((prev) => (prev === sectionIndex ? -1 : sectionIndex))
        }}>
        <span
          ref={drag}
          className='experience-grab'
          title='Drag to reorder'
          onClick={(e) => e.stopPropagation()}>
          ⋮⋮
        </span>
        <Field
          name={`sections[${sectionIndex}].title`}
          className='experience-cl-title'
          placeholder='Section title'
          aria-label='Section title'
          disabled={disabled}
          onClick={(e) => e.stopPropagation()}
          onFocus={() => {
            if (!disabled) setOpenIndex(sectionIndex)
          }}
          onBlur={fieldBlur}
        />
        <span className='experience-cl-count'>{itemLabel(itemCount)}</span>
        <button
          type='button'
          className='experience-cl-caret'
          aria-label={isOpen ? 'Collapse' : 'Expand'}
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation()
            if (disabled) return
            setOpenIndex((prev) => (prev === sectionIndex ? -1 : sectionIndex))
          }}>
          {isOpen ? '▾' : '▸'}
        </button>
        {canRemoveSection ? (
          <button
            type='button'
            className='experience-x'
            title='Delete section'
            aria-label='Delete section'
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation()
              if (!disabled) removeSection()
            }}>
            <ExperienceDeleteIcon />
          </button>
        ) : null}
      </div>
      <div className='experience-cl-sec-body'>
        <FieldArray name={`sections[${sectionIndex}].items`}>
          {({ push: pushItem, move: moveItem }) => (
            <>
              <div className='experience-cl-items'>
                {section.items.length === 0 ? (
                  <div className='experience-cl-empty'>No items yet.</div>
                ) : (
                  section.items.map((item, itemIndex) => (
                    <ChecklistItemRow
                      key={item._tempId || itemIndex}
                      sectionIndex={sectionIndex}
                      itemIndex={itemIndex}
                      moveItem={moveItem}
                      onDragEnd={onItemDragEnd}
                      canRemove={section.items.length > 1}
                      fieldBlur={fieldBlur}
                      disabled={disabled}
                      onRemove={() => onRemoveItem(itemIndex)}
                    />
                  ))
                )}
              </div>
              <button
                type='button'
                className='experience-cl-add'
                disabled={disabled}
                onClick={() => {
                  // Local draft only — empty items are stripped on save, so
                  // autosaving here would reinitialize and wipe the new row.
                  pushItem({
                    item: '',
                    _tempId: `item-${Date.now()}-${section.items.length}`,
                  })
                  window.setTimeout(() => {
                    const inputs = document.querySelectorAll(
                      `.experience-cl-sec.open .experience-cl-item input`,
                    )
                    const last = inputs[inputs.length - 1]
                    if (last) last.focus()
                  }, 0)
                }}>
                + Add item
              </button>
            </>
          )}
        </FieldArray>
        <div className='experience-cl-notes'>
          <label htmlFor={`cl-notes-${sectionIndex}`}>Notes</label>
          <Field
            as='textarea'
            id={`cl-notes-${sectionIndex}`}
            name={`sections[${sectionIndex}].notes`}
            placeholder='Optional notes for this section'
            disabled={disabled}
            onBlur={fieldBlur}
          />
        </div>
      </div>
    </div>
  )
}

const QuestTravelChecklist = forwardRef(
  (
    {
      questDetail,
      updateQuestDetails,
      isFormSubmitting,
      onPdfDownloadStateChange,
    },
    ref,
  ) => {
    const Error = useErrorService()
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)
    const [showPreview, setShowPreview] = useState(false)
    const [previewChecklistData, setPreviewChecklistData] = useState(null)
    const [openIndex, setOpenIndex] = useState(0)
    const [pendingDelete, setPendingDelete] = useState(null)
    const addSectionRef = useRef(null)
    const formikRef = useRef(null)
    const canDownloadPdfRef = useRef(false)
    const handlePreviewPdfRef = useRef(() => {})
    const handleDownloadPdfRef = useRef(() => {})
    const [initialValues, setInitialValues] = useState(() => {
      const sections =
        questDetail?.travelChecklist?.sections?.length > 0
          ? questDetail.travelChecklist.sections
          : defaultValues.sections
      return ensureChecklistFormShape({
        description: questDetail?.travelChecklist?.description || '',
        sections: normalizeChecklistSections(sections),
      })
    })
    const valuesRef = useRef(initialValues)

    useEffect(() => {
      const travelChecklist = questDetail?.travelChecklist
      const sections =
        travelChecklist?.sections?.length > 0
          ? travelChecklist.sections
          : defaultValues.sections
      const next = ensureChecklistFormShape({
        description: travelChecklist?.description || '',
        sections: normalizeChecklistSections(sections),
      })
      setInitialValues(next)
      valuesRef.current = next
      // Only remount sync when opening another experience — not after every save.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questDetail?._id, questDetail?.id])

    const questTitleForPdf = questDetail?.title || ''

    useImperativeHandle(ref, () => ({
      previewPdf: () => handlePreviewPdfRef.current(),
      downloadPdf: () => handleDownloadPdfRef.current(),
      addSection: () => addSectionRef.current?.(),
      canDownloadPdf: () => canDownloadPdfRef.current,
    }))

    const buildPreviewHtml = useCallback(async () => {
      if (!previewChecklistData) {
        return ''
      }

      return buildTravelChecklistPdfHtml({
        questTitle: questTitleForPdf,
        checklistData: previewChecklistData,
      })
    }, [previewChecklistData, questTitleForPdf])

    const closeDeleteModal = () => setPendingDelete(null)

    const confirmPendingDelete = () => {
      if (!pendingDelete || !formikRef.current) {
        closeDeleteModal()
        return
      }
      const formik = formikRef.current
      const current = formik.values
      const { type, sectionIndex, itemIndex } = pendingDelete
      closeDeleteModal()

      if (type === 'item') {
        const nextSections = current.sections.map((s, si) => {
          if (si !== sectionIndex) return s
          return {
            ...s,
            items: s.items.filter((_, ii) => ii !== itemIndex),
          }
        })
        const nextValues = { ...current, sections: nextSections }
        formik.setValues(nextValues, false)
        updateQuestDetails(buildSubmitPayload(nextValues), {
          silent: true,
        })
        return
      }

      if (type === 'section') {
        const nextSections = current.sections.filter(
          (_, i) => i !== sectionIndex,
        )
        const nextValues = {
          ...current,
          sections:
            nextSections.length > 0 ? nextSections : defaultValues.sections,
        }
        formik.setValues(nextValues, false)
        setOpenIndex((prev) => {
          if (current.sections.length <= 1) return 0
          if (prev > sectionIndex) return prev - 1
          if (prev === sectionIndex) {
            return Math.min(sectionIndex, current.sections.length - 2)
          }
          return prev
        })
        updateQuestDetails(buildSubmitPayload(nextValues), {
          silent: true,
        })
      }
    }

    const deleteTitle =
      pendingDelete?.type === 'section'
        ? 'Delete this checklist section?'
        : 'Delete this checklist item?'
    const deleteMessage =
      pendingDelete?.type === 'section'
        ? pendingDelete.label
          ? `\u201C${pendingDelete.label}\u201D and its items will be removed.`
          : 'This section and its items will be removed.'
        : pendingDelete?.label
        ? `\u201C${pendingDelete.label}\u201D will be removed.`
        : 'This item will be removed from the section.'

    return (
      <>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={async (values, { resetForm }) => {
            const syncFormValues = (latest) => {
              // Keep local draft sections/rows; only the API payload is filtered.
              const next = ensureChecklistFormShape(latest)
              resetForm({ values: next })
              setInitialValues(next)
              valuesRef.current = next
            }
            await runQueuedFormikSave({
              values,
              valuesRef,
              buildPayload: buildChecklistApiPayload,
              resetForm: ({ values: latest }) => syncFormValues(latest),
              setInitialValues: syncFormValues,
              save: (payload) => updateQuestDetails(payload, { silent: true }),
            })
          }}>
          {(formik) => {
            formikRef.current = formik
            const { values, handleSubmit, dirty } = formik
            valuesRef.current = formik.values
            const busy = !!isFormSubmitting
            const checklistDataForPdf = buildChecklistDataForPdf(values)
            const canDownloadPdf = hasChecklistContent(checklistDataForPdf)
            const fieldBlur = () => queueAutosaveOnBlur(formik)

            const persistValues = (nextValues) => {
              updateQuestDetails(buildSubmitPayload(nextValues), {
                silent: true,
              })
            }

            const persistCurrent = () => {
              if (!formikRef.current) return
              persistValues(formikRef.current.values)
            }

            const handleDownloadPdf = async () => {
              if (!canDownloadPdf || isDownloadingPdf) {
                return
              }

              setIsDownloadingPdf(true)
              if (typeof onPdfDownloadStateChange === 'function') {
                onPdfDownloadStateChange(true)
              }
              try {
                await downloadTravelChecklistPdf({
                  questTitle: questTitleForPdf,
                  checklistData: checklistDataForPdf,
                })
              } catch (e) {
                Error.showError({
                  message:
                    'Unable to download travel checklist PDF. Please try again.',
                })
              } finally {
                setIsDownloadingPdf(false)
                if (typeof onPdfDownloadStateChange === 'function') {
                  onPdfDownloadStateChange(false)
                }
              }
            }

            const handlePreviewPdf = () => {
              if (!canDownloadPdf) {
                return
              }
              setPreviewChecklistData(checklistDataForPdf)
              setShowPreview(true)
            }

            canDownloadPdfRef.current = canDownloadPdf
            handlePreviewPdfRef.current = handlePreviewPdf
            handleDownloadPdfRef.current = handleDownloadPdf

            return (
              <form onSubmit={handleSubmit} aria-busy={busy || undefined}>
                <FormikDirtyReporter id='prep-checklist' dirty={dirty} />
                <fieldset className='experience-form-fieldset'>
                  <FieldArray name='sections'>
                    {({ push, move }) => {
                      addSectionRef.current = () => {
                        // Local draft only — empty sections are filtered out on
                        // save; autosaving here removes the new section.
                        push({
                          title: '',
                          items: [{ item: '' }],
                          notes: '',
                        })
                        setOpenIndex(values.sections.length)
                      }
                      return (
                        <>
                          <div className='experience-f mb-4'>
                            <ExperienceInputField
                              name='description'
                              label='Description'
                              capitalizeFirstLetter={true}
                              placeholder='Complete these items before departure…'
                              as='textarea'
                              rows={3}
                              showErrorMessage={false}
                              displayFlex={false}
                              onBlur={fieldBlur}
                            />
                          </div>

                          {values.sections.map((section, sectionIndex) => (
                            <ChecklistSection
                              key={sectionIndex}
                              section={section}
                              sectionIndex={sectionIndex}
                              openIndex={openIndex}
                              setOpenIndex={setOpenIndex}
                              moveSection={(from, to) => {
                                move(from, to)
                                setOpenIndex((prev) => {
                                  if (prev === from) return to
                                  if (from < prev && to >= prev) return prev - 1
                                  if (from > prev && to <= prev) return prev + 1
                                  return prev
                                })
                              }}
                              onSectionDragEnd={persistCurrent}
                              onItemDragEnd={persistCurrent}
                              onRemoveItem={(itemIndex) => {
                                if (!formikRef.current) return
                                const current = formikRef.current.values
                                const itemText =
                                  current.sections[sectionIndex]?.items?.[
                                    itemIndex
                                  ]?.item
                                setPendingDelete({
                                  type: 'item',
                                  sectionIndex,
                                  itemIndex,
                                  label: truncateLabel(itemText),
                                })
                              }}
                              removeSection={() => {
                                if (!formikRef.current) return
                                const current = formikRef.current.values
                                const title =
                                  current.sections[sectionIndex]?.title
                                setPendingDelete({
                                  type: 'section',
                                  sectionIndex,
                                  label: truncateLabel(title),
                                })
                              }}
                              canRemoveSection={values.sections.length > 1}
                              fieldBlur={fieldBlur}
                            />
                          ))}

                          <button type='submit' hidden />
                        </>
                      )
                    }}
                  </FieldArray>
                </fieldset>
              </form>
            )
          }}
        </Formik>
        <PdfPreviewModal
          show={showPreview}
          onHide={() => {
            setShowPreview(false)
            setPreviewChecklistData(null)
          }}
          buildHtml={buildPreviewHtml}
          emptyMessage='No travel checklist content available for preview.'
        />
        <ExperienceConfirmModal
          show={pendingDelete != null}
          onHide={() => {
            if (!isFormSubmitting) closeDeleteModal()
          }}
          onConfirm={confirmPendingDelete}
          title={deleteTitle}
          message={deleteMessage}
          confirmLabel='Delete'
          isLoading={!!isFormSubmitting}
        />
      </>
    )
  },
)

QuestTravelChecklist.displayName = 'QuestTravelChecklist'

export default QuestHOC(QuestTravelChecklist)
